import { createAsyncThunk } from '@reduxjs/toolkit'
import { getFeatureFlag, getFeatureFlagKong } from 'features/Location/services/featureFlag'
import {
  getInventoryStatusNotesApi,
  type GetInventoryStatusNotesQueryParamsType,
} from 'features/Product/services/inventory'
import { getProductInventoryStatus } from 'features/Product/services/product'
import type {
  PurchaseOrderCheckQuantityInitialStateInterface,
  PurchaseOrderFeatureFlagKeyType,
} from 'features/PurchaseOrder/@types/CheckQuantity'
import {
  deletePurchaseOrderInboundItemApi,
  getPurchaseOrderInboundDetailsApi,
  getPurchaseOrderInboundItemsApi,
  postPurchaseOrderGenerateProductDetailApi,
  postPurchaseOrderInboundItemNonAssetApi,
  postPurchaseOrderInboundItemAssetApi,
  PostPurchaseOrderInboundItemRequestInterface,
  postPurchaseOrderInboundItemVerificationApi,
  PostPurchaseOrderInboundItemVerificationRequestInterface,
  patchRackInboundItemDetailApi,
  putSubmitPurchaseOrderInboundAssetCompleteApi,
  patchSubmitPurchaseOrderInboundNonAssetCompleteApi,
  type PostPurchaseOrderGenerateProductDetailRequestInterface,
  type SubmitPurchaseOrderInboundCompletePayloadInterface,
  type ApiResponseMessageOnlyInterface,
} from 'features/PurchaseOrder/services/checkQuantity'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'

export const SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY = 'purchaseOrderCheckQuantity'

export const fetchFeatureFlagByLocation = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/fetchFeatureFlagByLocation`,
  async (
    {
      locationId,
      featureFlagName,
    }: { locationId: number; featureFlagName: PurchaseOrderFeatureFlagKeyType },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await getFeatureFlag(locationId, featureFlagName)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchAccountInternalFeatureFlagByLocation = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/fetchAccountInternalFeatureFlagByLocation`,
  async (
    { locationId }: { locationId: number; featureFlagNames: PurchaseOrderFeatureFlagKeyType[] },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await getFeatureFlagKong(locationId)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchPurchaseOrderInboundDetails = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/fetchPurchaseOrderInboundDetails`,
  async (purchaseOrderId: number, { rejectWithValue }) => {
    try {
      const { data } = await getPurchaseOrderInboundDetailsApi(purchaseOrderId)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchPurchaseOrderInboundItems = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/fetchPurchaseOrderInboundItems`,
  async (purchaseOrderId: number, { rejectWithValue, getState }) => {
    const {
      purchaseOrderCheckQuantity: { purchaseOrderInboundItemsQueryParams },
    } = getState() as unknown as {
      purchaseOrderCheckQuantity: PurchaseOrderCheckQuantityInitialStateInterface
    }
    try {
      const { data } = await getPurchaseOrderInboundItemsApi(
        purchaseOrderId,
        purchaseOrderInboundItemsQueryParams,
      )
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const verifyPurchaseOrderInboundItem = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/verifyPurchaseOrderInboundItem`,
  async (
    {
      purchaseOrderId,
      payload,
    }: {
      purchaseOrderId: number
      payload: PostPurchaseOrderInboundItemVerificationRequestInterface
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await postPurchaseOrderInboundItemVerificationApi(purchaseOrderId, payload)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchProductInventoryStatusList = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/fetchProductInventoryStatusList`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getProductInventoryStatus()
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchProductInventoryStatusNotesList = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/fetchProductInventoryStatusNotesList`,
  async (queryParams: GetInventoryStatusNotesQueryParamsType, { rejectWithValue }) => {
    try {
      const { data } = await getInventoryStatusNotesApi(queryParams)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

interface SubmitPurchaseOrderInboundItemParamsInterface {
  purchaseOrderId: number
  itemId: number
  payload: PostPurchaseOrderInboundItemRequestInterface
}

export const submitPurchaseOrderInboundItem = createAsyncThunk<
  ApiResponseMessageOnlyInterface,
  SubmitPurchaseOrderInboundItemParamsInterface
>(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/submitPurchaseOrderInboundItem`,
  async ({ purchaseOrderId, itemId, payload }, { rejectWithValue, getState }) => {
    const { purchaseOrderCheckQuantity } = getState() as unknown as {
      purchaseOrderCheckQuantity: PurchaseOrderCheckQuantityInitialStateInterface // to prevent circular import dependencies in store.ts
    }
    const { purchaseOrderInboundDetails, featureFlagsLocation } = purchaseOrderCheckQuantity
    const isCrossDockActive = !!purchaseOrderInboundDetails?.attribute.is_xdock
    const isInboundAssetTracking =
      featureFlagsLocation.purchase_order_inbound_v2 && !isCrossDockActive

    const selectedApiToCall = isInboundAssetTracking
      ? postPurchaseOrderInboundItemAssetApi
      : postPurchaseOrderInboundItemNonAssetApi

    try {
      const { data } = await selectedApiToCall(purchaseOrderId, itemId, payload)
      toastSuccess(data?.message || 'Berhasil submit inbound item')
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

interface DeletePurchaseOrderInboundItemParamsInterface {
  purchaseOrderId: number
  inboundItemDetailId: number
}

export const deletePurchaseOrderInboundItem = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/deletePurchaseOrderInboundItem`,
  async (
    { purchaseOrderId, inboundItemDetailId }: DeletePurchaseOrderInboundItemParamsInterface,
    { rejectWithValue },
  ) => {
    try {
      const { data } = await deletePurchaseOrderInboundItemApi(purchaseOrderId, inboundItemDetailId)
      toastSuccess(data.message)
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

interface UpdateRackInboundItemDetailParamsInterface {
  purchaseOrderInboundItemDetailId: number
  rackId: number
}

export const updateRackInboundItemDetail = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/updateRackInboundItemDetail`,
  async (
    { purchaseOrderInboundItemDetailId, rackId }: UpdateRackInboundItemDetailParamsInterface,
    { rejectWithValue },
  ) => {
    try {
      const { data } = await patchRackInboundItemDetailApi(purchaseOrderInboundItemDetailId, {
        rack_id: rackId,
      })
      toastSuccess(data.message)
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const generatePurchaseOrderProductDetailToPrint = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/generatePurchaseOrderProductDetailToPrint`,
  async (payload: PostPurchaseOrderGenerateProductDetailRequestInterface, { rejectWithValue }) => {
    try {
      const { data } = await postPurchaseOrderGenerateProductDetailApi(payload)
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const submitPurchaseOrderInboundAssetComplete = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/submitPurchaseOrderInboundAssetComplete`,
  async (
    {
      purchaseOrderId,
      payload,
    }: {
      purchaseOrderId: number
      payload: SubmitPurchaseOrderInboundCompletePayloadInterface
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await putSubmitPurchaseOrderInboundAssetCompleteApi(purchaseOrderId, payload)
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const submitPurchaseOrderInboundNonAssetComplete = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY}/submitPurchaseOrderInboundNonAssetComplete`,
  async (
    {
      purchaseOrderId,
      payload,
    }: {
      purchaseOrderId: number
      payload: SubmitPurchaseOrderInboundCompletePayloadInterface
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await patchSubmitPurchaseOrderInboundNonAssetCompleteApi(
        purchaseOrderId,
        payload,
      )
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
